import {
  type Mutation,
  type MutationKey,
  type QueryClient,
  type UseMutationOptions,
  useMutationState,
  useQueryClient,
} from '@tanstack/react-query';
import { getApiErrorMessage } from 'utils/errorHandler';

export const isKeptFailure = ({ meta, state }: Mutation) => !!meta?.keepFailure && state.status === 'error';

// Keeps a rejected upload cached, as a failed row, until it is retried or dismissed.
export const keepingFailures = <TData, TError, TVariables, TOnMutateResult>(
  options: UseMutationOptions<TData, TError, TVariables, TOnMutateResult> = {},
): UseMutationOptions<TData, TError, TVariables, TOnMutateResult> => ({
  ...options,
  gcTime: Infinity,
  meta: { ...options.meta, keepFailure: true },
  // gcTime would otherwise keep succeeded uploads, and their files, cached too.
  onSettled: (data, error, variables, onMutateResult, context) => {
    const mutationCache = context.client.getMutationCache();
    const succeeded =
      !error &&
      mutationCache.find({
        predicate: ({ state }) => state.status === 'pending' && state.variables === variables,
      });
    if (succeeded) mutationCache.remove(succeeded);
    return options.onSettled?.(data, error, variables, onMutateResult, context);
  },
});

// For batches that reject items one by one: each becomes its own failed upload, so it is retried alone.
export const keepItemFailures = <TVariables>(
  queryClient: QueryClient,
  mutationKey: MutationKey,
  failures: { variables: TVariables; error: Error }[],
) =>
  failures.forEach(({ variables, error }) =>
    queryClient
      .getMutationCache()
      .build<unknown, Error, TVariables, unknown>(queryClient, keepingFailures({ mutationKey }), {
        status: 'error',
        variables,
        error,
        failureReason: error,
        failureCount: 1,
        data: undefined,
        context: undefined,
        isPaused: false,
        submittedAt: Date.now(),
      }),
  );

export const hasKeptUploadFailures = (queryClient: QueryClient) =>
  queryClient.getMutationCache().getAll().some(isKeptFailure);

export const useKeptUploadFailures = <TPayload>(
  mutationKey: MutationKey,
  isFor: (payload: TPayload) => boolean,
  retry: (payload: TPayload) => void,
) => {
  'use memo';
  const mutationCache = useQueryClient().getMutationCache();
  const failures = useMutationState({
    filters: {
      mutationKey,
      predicate: mutation => isKeptFailure(mutation) && isFor(mutation.state.variables as TPayload),
    },
    select: mutation => mutation,
  });

  return failures.map(mutation => {
    const payload = mutation.state.variables as TPayload;
    const dismiss = () => mutationCache.remove(mutation);
    return {
      key: mutation.mutationId,
      payload,
      reason: getApiErrorMessage(mutation.state.error, 'Upload failed'),
      onRetry: () => {
        dismiss();
        retry(payload);
      },
      onDismiss: dismiss,
    };
  });
};
